import { Card, CardHeader, CardTitle, CardToolbar } from '@/components/Card'
import Loading from '@/components/Loading'
import {
    groupByBoleto,
    groupByPix,
    isFaturaAberta,
    sortByExpiration,
} from '@/services/fatura'
import type { Fatura } from '@/types/fatura'
import type { PagamentoBoleto, PagamentoCartao, PagamentoPix } from '@/types/pagamento'
import { formatDate } from '@/utils'
import { useState } from 'react'
import DetalheFaturaModal from './DetalheFaturaModal'
import FaturasAbertasCard from './FaturaAbertaCard'
import DetalhesPagamentoAberto from './DetalhesPagamentoAberto'
import PagamentoResultadoModal from './PagamentoResultadoModal'
import PagamentoPanel from './PagamentoPanel'
import FaturaValorResumo from './FaturaValorResumo'
import './FaturasView.scss'

interface FaturasViewProps {
    faturas: Fatura[]
    isLoading: boolean
    onRefresh: () => Promise<void>
    clienteHash?: string
    dataLimite?: string
}

export default function FaturasView({
    faturas,
    isLoading,
    onRefresh,
    clienteHash,
    dataLimite,
}: FaturasViewProps) {
    const [detalhesFatura, setDetalhesFatura] = useState<Fatura['Detalhes'] | null>(null)
    const [resultadoPagamento, setResultadoPagamento] = useState<false | PagamentoPix | PagamentoBoleto | PagamentoCartao>(false)

    const groupedByBoleto = groupByBoleto(faturas)
    const groupedByPix = groupByPix(faturas)
    const faturasAbertas = faturas.filter(isFaturaAberta).sort(sortByExpiration)

    const renderGrupoDetalhado = (titulo: string, tipo: 'boleto' | 'pix', grupos: Fatura[][]) => {
        if (grupos.length === 0) return null

        return (
            <section className="faturas-view-section" aria-label={titulo}>
                <Card style={{ marginBottom: 12 }}>
                    <CardHeader>
                        <CardTitle>{titulo}</CardTitle>
                        <CardToolbar>
                            <FaturaValorResumo
                                faturas={grupos.flat()}
                                variant="total-only"
                            />
                        </CardToolbar>
                    </CardHeader>
                </Card>

                {grupos.map(grupo => (
                    <div
                        key={tipo === 'boleto' ? grupo[0].boleto?.CdBoleto : grupo[0].pix?.CdPix}
                        style={{ marginBottom: 10 }}
                    >
                        <div className="faturas-aberto-grid">
                            <div className="faturas-aberto-list">
                                <FaturasAbertasCard faturas={grupo} />
                            </div>
                            <div className="faturas-aberto-pagamento">
                                <DetalhesPagamentoAberto tipo={tipo} faturas={grupo} onRefresh={onRefresh} />
                            </div>
                        </div>
                    </div>
                ))}
            </section>
        )
    }

    return (
        <div className="faturas-view">
            {resultadoPagamento && (
                <PagamentoResultadoModal
                    completed={resultadoPagamento}
                    onClose={() => setResultadoPagamento(false)}
                />
            )}

            {detalhesFatura && (
                <DetalheFaturaModal
                    detalhesFatura={detalhesFatura}
                    onClose={() => setDetalhesFatura(null)}
                />
            )}

            {renderGrupoDetalhado('Boletos a pagar', 'boleto', groupedByBoleto)}
            {renderGrupoDetalhado('Pix a pagar', 'pix', groupedByPix)}

            {/* ── Faturas em aberto + painel de pagamento ───────────────── */}
            <section className="faturas-view-section" aria-label="Faturas em aberto">
                <Card style={{ marginBottom: 12 }}>
                    <CardHeader>
                        <CardTitle>
                            Faturas em aberto
                            {dataLimite && (
                                <span className="faturas-view-sublabel">
                                    até {formatDate(dataLimite)}
                                </span>
                            )}
                        </CardTitle>
                        {faturasAbertas.length > 0 && (
                            <CardToolbar>
                                <FaturaValorResumo faturas={faturasAbertas} variant="total-only" />
                            </CardToolbar>
                        )}
                    </CardHeader>
                </Card>

                <div className="faturas-aberto-grid">
                    {/* Left: lista de faturas com detalhes expandidos */}
                    <div className="faturas-aberto-list">
                        {isLoading && <Loading type="primary" />}
                        {!isLoading && faturasAbertas.length === 0 && (
                            <p className="faturas-view-empty">Nenhuma fatura em aberto.</p>
                        )}
                        {!isLoading && faturasAbertas.length > 0 && (
                            <FaturasAbertasCard faturas={faturasAbertas} />
                        )}
                    </div>

                    {/* Right: painel de pagamento (sticky) */}
                    {!isLoading && faturasAbertas.length > 0 && (
                        <div className="faturas-aberto-pagamento">
                            <PagamentoPanel
                                faturas={faturasAbertas}
                                onPaymentComplete={onRefresh}
                                clienteHash={clienteHash}
                                onPaymentResult={setResultadoPagamento}
                                resultModalOpen={Boolean(resultadoPagamento)}
                            />
                        </div>
                    )}
                </div>
            </section>
        </div>
    )
}
